import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { t } from "i18next";

import FormEvaluationAnthropometric from "@/shared/components/organisms/FormEvaluationAnthropometric";
import FormValuationStateHealth from "@/shared/components/organisms/FormValuationStateHealth";
import { ListTrainingPlan } from "@/shared/components/organisms/ListTrainingPlan";
import { ButtonSecondary } from "@/shared/components/atoms/ButtonSecondary";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { TabsUser } from "@/shared/components/organisms/TabsUser";

import SearchPlanComponent from "./SearchPlanComponent";
import { TrainingWizard } from "@/shared/components/organisms/TrainingWizard";
import { useFinishTrainingWizardMutation } from "@/app/training-wizard/training-wizard.api";
import { usePersonalInformationQuery } from "@/app/personal-informations/personal-informations.api";
import { useWeightQuery } from "@/app/weights/weights";
import { useHeightQuery } from "@/app/heights/heights.api";
import { useGetUserInfoQuery } from "@/app/users/users.api";
import { createSearchParams, useNavigate } from "react-router-dom";

import {
  useGetProgressionRoutineQuery,
  useGetUserPlaningQuery,
} from "../api/user-active-plan.api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDumbbell, faWeightScale } from "@fortawesome/free-solid-svg-icons";
import PreviewRoutine from "./PreviewRoutine";
import ValorationStateEalth from "@/shared/components/organisms/ValorationStateEalth";
import { NutritionWizard } from "@/shared/components/organisms/NutritionWizard";
import { useTrainingProgressQuery } from "@/app/training-progress/training-progress.api";
import { NutritionOverview } from "@/shared/components/organisms/NutritionOverview";
import {
  useAssignNutritionMutation,
  useDeleteNutritionMutation,
  useDuplicateNutritionMutation,
  useNutritionOverviewQuery,
  useNutritionsQuery,
} from "@/app/nutrition/nutrition.api";
import { NutritionSearch } from "@/shared/components/organisms/NutritionSearch";
import { toasts } from "@/shared/utils/toasts";
import { useNutritionProgressesQuery } from "@/app/progress-nutritions/progress-nutritions.api";
import { NutritionProgressList } from "@/shared/components/organisms/NutritionProgressList";
import { NutritionPlan, NutritionProgress } from "@/app/nutrition/plan";
import { EditorType } from "@/app/nutrition/nutrition-editor";

export type CustomerExperienceProps = {
  user?: any;
  userId?: any;
};

const CustomerExperience: FC<CustomerExperienceProps> = ({
  user,
  userId,
}: {
  userId: number | any;
}) => {
  const id = userId;
  const navigate = useNavigate();

  const [nutritionOverviewPlan, setNutritionOverviewPlan] =
    useState<NutritionPlan>();
  const [nutritionOverviewId, setNutritionOverviewId] = useState<
    undefined | number
  >(undefined);

  const [showNutritionSearch, setShowNutritionSearch] = useState(false);
  const [nutritionPlansPage, setNutritionPlansPage] = useState(1);

  const nutritionProgressesQuery = useNutritionProgressesQuery(
    {
      userId: user?.id,
    },
    {
      skip: !user?.id,
    }
  );

  const nutritionPlans = useMemo(() => {
    if (!nutritionProgressesQuery.isSuccess) return [];

    return nutritionProgressesQuery.data.map((item: any) => ({
      ...item,
      namePlan: item?.plan?.plan,
    }));
  }, [nutritionProgressesQuery]);

  const nutritionsQuery = useNutritionsQuery({ userId: user?.id });
  const nutritionOverviewQuery = useNutritionOverviewQuery({
    id: nutritionOverviewPlan?.id,
    nutritionTable: nutritionOverviewPlan?.nutritionTable,
  });

  const [deleteNutrition, deleteNutritionResult] = useDeleteNutritionMutation();
  const [assignNutrition, assignNutritionResult] = useAssignNutritionMutation();
  const [duplicateNutrition, duplicateNutritionResult] =
    useDuplicateNutritionMutation();

  const handleEditNutrition = useCallback((plan) => {
    navigate({
      pathname: "/nutrition",
      search: createSearchParams({
        user_id: user?.id,
        id: plan.id,
        type: EditorType.Edit,
      }).toString(),
    });
  }, []);

  const handleOverviewNutrition = useCallback(
    (plan: NutritionPlan) => {
      setNutritionOverviewPlan(() => plan);
    },
    [nutritionOverviewPlan]
  );

  const handleDeleteNutrition = useCallback(
    (plan) => {
      deleteNutrition({
        id: plan.id,
        userId: user?.id,
      });
    },
    [user]
  );

  useEffect(() => {
    if (deleteNutritionResult.isSuccess) {
      toasts.success(
        t("contexts.nutrition.delete_plan_success"),
        t("contexts.nutrition.delete_plan_success_description")
      );
    }

    if (deleteNutritionResult.isError) {
      toasts.danger(
        t("contexts.nutrition.delete_plan_error"),
        t("contexts.nutrition.delete_plan_error_description")
      );
    }
  }, [deleteNutritionResult]);

  const handleAssignNutrition = useCallback(
    (plan: NutritionPlan) => {
      assignNutrition({
        id: plan.id,
        userId: user?.id,
      });
    },
    [user]
  );

  useEffect(() => {
    if (assignNutritionResult.isSuccess) {
      toasts.success(
        t("contexts.nutrition.assign_plan_success"),
        t("contexts.nutrition.assign_plan_success_description")
      );
    }

    if (assignNutritionResult.isError) {
      toasts.danger(
        t("contexts.nutrition.assign_plan_error"),
        t("contexts.nutrition.assign_plan_error_description")
      );
    }
  }, [assignNutritionResult]);

  const handleDuplicateNutrition = useCallback(
    (plan) => {
      duplicateNutrition({
        id: plan.id,
        userId: user?.id,
      });
    },
    [user]
  );

  useEffect(() => {
    if (duplicateNutritionResult.isSuccess) {
      toasts.success(
        t("contexts.nutrition.duplicate_plan_success"),
        t("contexts.nutrition.duplicate_plan_success_description")
      );
    }

    if (duplicateNutritionResult.isError) {
      toasts.danger(
        t("contexts.nutrition.duplicate_plan_error"),
        t("contexts.nutrition.duplicate_plan_error_description")
      );
    }
  }, [duplicateNutritionResult]);

  const userQuery = useGetUserInfoQuery({ id: userId });

  const trainingProgressQuery = useTrainingProgressQuery({
    userId: user?.id,
    status: 1,
  });

  const { data: plans, isLoading } = useGetUserPlaningQuery({ id });

  const [openModalTrainingPlan, setOpenModalTrainingPlan] = useState(false);
  const [openModalTrainingDescription, setOpenModalTrainingDescription] =
    useState(false);
  const [openNewPlanTraining, setOpenNewPlanTraining] = useState(false);
  const [openNutritionFile, setOpenNutritionFile] = useState(false);
  const [openNewPlanNutrition, setOpenNewPlanNutrition] = useState(false);
  const [loaderWizard, setLoaderWizard] = useState(false);

  const [cicle, setCicle] = useState("no");

  const [finishTrainingWizard, finishTrainingWizardResult] =
    useFinishTrainingWizardMutation();
  const personalInformationQuery = usePersonalInformationQuery({
    userId: id,
    status: 1,
  });

  const weightQuery = useWeightQuery(
    {
      personalInformationId: personalInformationQuery.data?.id,
      status: 1,
    },
    { skip: !personalInformationQuery.isSuccess }
  );

  const heightQuery = useHeightQuery(
    {
      personalInformationId: personalInformationQuery.data?.id,
      status: 1,
    },
    { skip: !personalInformationQuery.isSuccess }
  );

  const [wizardData, setWizardData] = useState({
    height: "170",
    weight: "70",
    age: "23",
    genre: "hombre",
    objective: "hypertrophy",
  });

  useEffect(() => {
    if (
      finishTrainingWizardResult.isSuccess &&
      finishTrainingWizardResult.data
    ) {
      const data = finishTrainingWizardResult.data;
      const searchParams = new URLSearchParams({
        user_id: id,
        training_progress_id: data?.trainingProgressId,
        id: data?.id,
        position: data?.position,
        name: data?.name,
        period: cicle == "si" ? "1" : "0",
        height: wizardData.height,
        weight: wizardData.weight,
        age: wizardData.age,
        objective: wizardData.objective,
        genre: wizardData.genre,
        is_wizard: "1",
        planet_posible: activePlan ? "1" : "0",
      });

      navigate(`/training?${searchParams.toString()}`);
    }
  }, [finishTrainingWizardResult, navigate, userId, finishTrainingWizard]);

  const handleOpenModalTrainingDescription = () =>
    setOpenModalTrainingDescription(!openModalTrainingDescription);

  const handleOpenNewPlanTraining = () =>
    setOpenNewPlanTraining(!openNewPlanTraining);

  //TABS GENERALES
  const [selectedTab, setSelectedTab] = useState(0);
  const tabs = [
    {
      id: 1,
      name: t("anthropometric_tabs.tab_1"),
      description: t("anthropometric_tabs.tab_description_1"),
    },
    {
      id: 2,
      name: t("anthropometric_tabs.tab_2"),
      description: t("anthropometric_tabs.tab_description_2"),
    },
    {
      id: 3,
      name: t("anthropometric_tabs.tab_3"),
      description: t("anthropometric_tabs.tab_description_3"),
    },
  ];

  const handleTabChange = (tabId: any) => {
    setSelectedTab(tabId);
  };

  /*   const [fumador, setFumador] = useState(false);
  const optionsAlcohol = [
    { id: 1, name: t("user.views.select") },
    { id: 2, name: t("general.yes") },
    { id: 3, name: t("general.no") },
    { id: 4, name: t("user.views.select_combobox_sporadic") },
  ]; */
  const handleOpenModalTrainingPlan = () =>
    setOpenModalTrainingPlan(!openModalTrainingPlan);

  const activePlan = plans && plans?.some((x: any) => x.state === "ACTIVO");
  const activePlanItem = plans?.find((x: any) => x.state === "ACTIVO");

  console.log("planeswidzar ====", plans);

  const { data: progressRoutine, isLoading: isLoadingProgress } =
    useGetProgressionRoutineQuery({
      userId: userId,
    });

  const newProgression = () => {
    if (progressRoutine && !isLoadingProgress) {
      navigate(
        `/training?p-customize/edit?progress=true&user_id=${userId}&position=${progressRoutine?.p_customize?.position}&id=${progressRoutine?.p_customize?.id}`
      );
    }
  };

  const [openPreview, setOpenPreview] = useState<any | null>({
    position: null,
    state: false,
  });

  const handleOpenPreviewPlan = (position: number) => {
    setOpenPreview({
      position,
      state: true,
    });
  };

  console.log("activePlanItem", activePlanItem);
  const editActivePlan = () => {
    navigate(
      `/training?p-customize/edit?edit=true&active=true&position=${activePlanItem?.position}&id=${activePlanItem?.id}&user_id=${userId}`
    );
  };

  const wizardSubmit = (values: any) => {
    if (values?.position) {
      navigate(
        `/admin/users/p-customize/edit?edit=true&algorithm=true&plan=${values?.days?.plan ?? values?.plan}&position=${values?.position}`
      );
    }
  };

  const [trainingWizardVisible, setTrainingWizardVisible] = useState(false);
  const [nutritionWizardVisible, setNutritionWizardVisible] = useState(false);

  const completedProgressNutrition = useMemo(() => {
    return nutritionProgressesQuery.data?.find(
      (progress) => progress.status === 0
    );
  }, [nutritionProgressesQuery]);

  const activeProgressNutrition = useMemo(() => {
    return nutritionProgressesQuery.data?.find(
      (progress) => progress.status === 1
    );
  }, [nutritionProgressesQuery]);

  const plannedProgressNutrition = useMemo(() => {
    return nutritionProgressesQuery.data?.find(
      (progress) => progress.status === 3
    );
  }, [nutritionProgressesQuery]);

  const handleNutritionCreateWithAlgorithm = useCallback(() => {
    setNutritionWizardVisible(!nutritionWizardVisible);
  }, [nutritionWizardVisible]);

  const handleNutritionCreateFromScratch = useCallback(() => {
    navigate({
      pathname: "/nutrition",
      search: createSearchParams({
        user_id: user?.id,
        type: EditorType.Scratch,
      }).toString(),
    });
  }, []);

  const handleNutritionUpdateAcive = useCallback(() => {
    navigate({
      pathname: "/nutrition",
      search: createSearchParams({
        user_id: user?.id,
        id: activeProgressNutrition?.plan?.id,
        type: EditorType.Active,
      }).toString(),
    });
  }, [activeProgressNutrition]);

  const handleNutritionUpdateCompleted = useCallback(() => {
    navigate({
      pathname: "/nutrition",
      search: createSearchParams({
        user_id: user?.id,
        id: completedProgressNutrition?.plan?.id,
        type: EditorType.Active,
      }).toString(),
    });
  }, [completedProgressNutrition]);

  const hadleNutritionUpdatePlanned = useCallback(() => {
    navigate({
      pathname: "/nutrition",
      search: createSearchParams({
        user_id: user?.id,
        id: plannedProgressNutrition?.plan?.id,
        type: EditorType.Planned,
      }).toString(),
    });
  }, [plannedProgressNutrition]);

  const handleNutritionProgressView = useCallback(
    (progress: NutritionProgress) => {
      setNutritionOverviewPlan(progress.plan);
    },
    []
  );

  const handleNutritionOnSearch = useCallback(() => {
    setShowNutritionSearch((showNutritionSearch) => !showNutritionSearch);
  }, [showNutritionSearch]);

  const handleNutritionViewFile = useCallback(() => {
    setOpenNutritionFile(!openNutritionFile);
  }, [openNutritionFile]);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-20">
        <ListTrainingPlan
          onClickPlan={handleOpenModalTrainingDescription}
          onCreate={handleOpenNewPlanTraining}
          title={t("user.views.workout_plan")}
          button={
            <ButtonSecondary
              text={t("user.views.activities_calendar")}
              size="medium"
            />
          }
          newPlanOptions={[
            {
              id: 1,
              label: t("training.wizard.new_plan"),
              loader: isLoadingProgress,
              action: handleOpenNewPlanTraining,
              icon: (
                <FontAwesomeIcon icon={faDumbbell} className="text-gray-400" />
              ),
            },
            activePlanItem && {
              id: 2,
              label: t("training.wizard.new_progress"),
              action: newProgression,
              icon: (
                <FontAwesomeIcon
                  icon={faWeightScale}
                  className="text-gray-400"
                />
              ),
            },

            {
              id: 3,
              label: t("training.wizard.new_training"),
              action: () => navigate(`/training?user_id=${userId}&new=true`),
              icon: (
                <FontAwesomeIcon icon={faDumbbell} className="text-gray-400" />
              ),
            },
          ]}
          onSearch={handleOpenModalTrainingPlan}
          onClickAdd={handleOpenNewPlanTraining}
          options={plans ?? []}
          loader={isLoading}
          onTitleClick={(position) => handleOpenPreviewPlan(position)}
          onNewPlan={handleOpenNewPlanTraining}
          onEditActive={activePlan ? editActivePlan : undefined}
        />

        <NutritionProgressList
          isLoading={nutritionProgressesQuery.isLoading}
          progress={nutritionProgressesQuery.data ?? []}
          onCreateWithAlgorithm={handleNutritionCreateWithAlgorithm}
          onCreateFromScratch={handleNutritionCreateFromScratch}
          onUpdateActive={handleNutritionUpdateAcive}
          onUpdatePlanned={hadleNutritionUpdatePlanned}
          onUpdateCompleted={handleNutritionUpdateCompleted}
          onSearch={handleNutritionOnSearch}
          onView={handleNutritionProgressView}
          onViewFile={handleNutritionViewFile}
        />
      </div>

      <div className="mt-10 border-t border-gray-200">
        <TabsUser
          tabs={tabs}
          selectedTab={selectedTab}
          handleTabChange={handleTabChange}
        />
      </div>

      <div className="grid grid-cols-1 mt-10">
        {selectedTab === 1 ? (
          <FormEvaluationAnthropometric user={userQuery?.data} />
        ) : selectedTab === 2 ? (
          <FormValuationStateHealth user_id={id} />
        ) : selectedTab === 3 ? (
          <ValorationStateEalth />
        ) : (
          <></>
        )}
      </div>

      <SearchPlanComponent
        onOpenPreviewPlan={(value: any) => handleOpenPreviewPlan(value)}
        user_id={id}
        openModalTrainingPlan={openModalTrainingPlan}
        setOpenModalTrainingPlan={setOpenModalTrainingPlan}
        setOpenNewPlanTraining={setOpenNewPlanTraining}
      />

      <TrainingWizard
        onSubmit={async (values: any) => {
          setCicle(values?.cicle ?? "no");
          console.log("values", values);
          try {
            setLoaderWizard(true);
            setWizardData(values);

            await finishTrainingWizard({ ...values, userId: id ?? userId });
          } catch (error) {
            console.log("error", error);
          } finally {
            setLoaderWizard(false);
          }
        }}
        loader={loaderWizard}
        visible={openNewPlanTraining}
        setVisible={setOpenNewPlanTraining as any}
        user={userQuery?.data}
        personalInformation={personalInformationQuery.data}
        weight={weightQuery.data}
        height={heightQuery.data}
        /* @ts-ignore */
        /*      onSubmit={(value: any) => wizardSubmit(value)} */
      />

      <NutritionWizard
        isLoading={trainingProgressQuery.isLoading}
        onSubmit={() => {}}
        visible={nutritionWizardVisible}
        setVisible={setNutritionWizardVisible}
        trainingProgress={trainingProgressQuery?.data}
        user={userQuery?.data?.data?.user}
        personalInformation={personalInformationQuery.data}
        weight={weightQuery.data}
        height={heightQuery.data}
      />

      <DialogCreate
        tittle="Nutricion"
        tittleButtonCreate="Create"
        tittleButtonCancel={t("general.cancel")}
        open={showNutritionSearch}
        setOpen={() => setShowNutritionSearch(!showNutritionSearch)}
        OnclickCancel={() => setShowNutritionSearch(false)}
      >
        <NutritionSearch
          isLoading={nutritionsQuery.isLoading}
          onOverview={handleOverviewNutrition}
          onDelete={handleDeleteNutrition}
          onAssign={handleAssignNutrition}
          onDuplicate={handleDuplicateNutrition}
          onEdit={handleEditNutrition}
          pagination={{
            ...nutritionsQuery?.data,
            currentPage: nutritionPlansPage,
            onPageChange: (page) => setNutritionPlansPage(page),
          }}
        />
      </DialogCreate>
      <DialogCreate
        tittle="Nutricion"
        tittleButtonCreate="Create"
        tittleButtonCancel={t("general.cancel")}
        open={!!nutritionOverviewPlan}
        setOpen={() => setNutritionOverviewPlan(undefined)}
        OnclickCancel={() => setNutritionOverviewPlan(undefined)}
      >
        <NutritionOverview
          isLoading={
            nutritionOverviewQuery.isLoading ||
            nutritionOverviewQuery.isFetching
          }
          nutrition={nutritionOverviewQuery.data?.nutrition}
          macros={nutritionOverviewQuery.data?.macros}
          supplementations={nutritionOverviewQuery.data?.supplementations}
          nutritionalInformations={
            nutritionOverviewQuery.data?.nutritionalInformations
          }
          equivalences={nutritionOverviewQuery.data?.equivalences}
        />
      </DialogCreate>

      <DialogCreate
        tittle={t("chat.app.training_plan")}
        tittleButtonCancel={t("general.cancel")}
        tittleButtonCreate={t("general.create")}
        open={openPreview?.state}
        setOpen={(open) => setOpenPreview({ position: null, state: open })}
        OnclickCancel={() => setOpenPreview({ position: null, state: false })}
      >
        <PreviewRoutine
          position={openPreview?.position}
          user_id={id}
          setPreviewRoutine={setOpenPreview}
        />
      </DialogCreate>
    </>
  );
};

export default CustomerExperience;
